import * as React from 'react';

/** Where an element was in a list that is a focus group (`data-focus-group`). */
export type ListPlace = {
  // The group when the place was recorded, and how to find it when it mounts
  // again: its name, which of the groups with that name it was, and the
  // groups with that name that were there then.
  group: HTMLElement;
  name: string;
  nth: number;
  named: ReadonlyArray<HTMLElement>;
  // The item that held the element, and its index. -1 when the element is in
  // the group but in no item.
  item: Element | null;
  itemIndex: number;
  // The control's index in the item, from the start and from the end. -1
  // when the element is not one of the item's controls (the item itself).
  control: number;
  controlFromEnd: number;
};

/** Where an element was, so that focus can go back there when it is gone. */
export type FocusPlace = {
  // The element's ancestors when the place was recorded, the nearest first.
  // Where to look for a `data-focus-fallback` once the element is gone.
  ancestors: Array<Element>;
  list: ListPlace | null;
};

/**
 * Focus recovery (see `FocusRecoveryProvider`). A dialog asks it first when
 * its opener is gone, so that one piece of code decides where focus goes.
 */
export type FocusRecovery = {
  // Where the element is now.
  placeOf: (element: Element) => FocusPlace;
  // Moves focus to the element that now stands for `place`, and says whether
  // it found one.
  recover: (place: FocusPlace | null) => boolean;
};

// There is none outside a `FocusRecoveryProvider` (a player, say): a dialog
// then uses `focusFallback` alone.
export const FocusRecoveryContext = React.createContext<FocusRecovery | null>(
  null,
);
